import type { IAdherenceSettingQueueLimit } from 'infrastructure/interfaces';

// Working window: 08:00–18:00 → hours 8..17 (`to` = `from` + 1), 10 slots/day.
export const WORK_START = 8;
export const WORK_END = 18; // exclusive
export const HEATMAP_MAX = 1000;

// Index === backend dayOfWeek (0 = Sunday … 6 = Saturday, matches Date.getDay()).
export const DAY_ORDER = [0, 1, 2, 3, 4, 5, 6];

export const HOURS = Array.from(
  { length: WORK_END - WORK_START },
  (_, i) => WORK_START + i,
);

export type QueueLimitsView = 'grid' | 'table' | 'day';

// dayOfWeek → hour → value
export type LimitsMap = Record<number, Record<number, number>>;
export type GuidMap = Record<number, Record<number, string>>;

export interface IDayLabel {
  full: string;
  short: string;
}

export interface ICellVisual {
  bg: string;
  color: string;
}

export interface IWindow {
  from: number;
  to: number;
  value: number;
}

export const cellKey = (day: number, hour: number) => `${day}-${hour}`;

export const buildMaps = (data: IAdherenceSettingQueueLimit[]) => {
  const limits: LimitsMap = {};
  const guids: GuidMap = {};
  DAY_ORDER.forEach((d) => {
    limits[d] = {};
    guids[d] = {};
  });
  data.forEach((row) => {
    if (row.from < WORK_START || row.from >= WORK_END) return;
    if (!limits[row.dayOfWeek]) {
      limits[row.dayOfWeek] = {};
      guids[row.dayOfWeek] = {};
    }
    limits[row.dayOfWeek][row.from] = row.limit;
    guids[row.dayOfWeek][row.from] = row.guid;
  });
  return { limits, guids };
};

export const cloneLimits = (source: LimitsMap): LimitsMap => {
  const out: LimitsMap = {};
  Object.keys(source).forEach((key) => {
    const d = Number(key);
    out[d] = { ...source[d] };
  });
  return out;
};

const RAMP_LOW = [255, 243, 229];
const RAMP_HIGH = [251, 147, 24];

const toHex = (rgb: number[]) =>
  `#${rgb
    .map((x) => {
      const h = Math.round(x).toString(16);
      return h.length < 2 ? `0${h}` : h;
    })
    .join('')}`;

const mix = (a: number[], b: number[], t: number) =>
  toHex([0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * t));

export const cellVisual = (value: number, max = HEATMAP_MAX): ICellVisual => {
  if (!value || value <= 0) return { bg: '#f3f2f9', color: '#9692a1' };
  const t = Math.min(1, Math.max(0, value / Math.max(20, max)));
  return {
    bg: mix(RAMP_LOW, RAMP_HIGH, t),
    color: t > 0.52 ? '#ffffff' : '#2c2543',
  };
};

// Parse an inline / bulk edit. Returns null when the input should be ignored
// (empty or non-numeric); never produces a negative or fractional value.
export const parseLimit = (raw: string): number | null => {
  const trimmed = String(raw).trim();
  if (trimmed === '') return null;
  const n = Math.floor(Number(trimmed));
  if (Number.isNaN(n)) return null;
  return Math.max(0, n);
};

// Collapse a day's hourly values into contiguous From→To windows of equal value.
export const windowsFor = (values: Record<number, number>): IWindow[] => {
  const res: IWindow[] = [];
  let i = 0;
  while (i < HOURS.length) {
    let j = i;
    while (j + 1 < HOURS.length && values[HOURS[j + 1]] === values[HOURS[i]]) {
      j += 1;
    }
    res.push({
      from: HOURS[i],
      to: HOURS[j] + 1,
      value: values[HOURS[i]] ?? 0,
    });
    i = j + 1;
  }
  return res;
};

export const fmtHour = (h: number) => `${h < 10 ? `0${h}` : h}:00`;
export const hourHeaderLabel = (h: number) => (h < 10 ? `0${h}` : `${h}`);

export const fmt12 = (h: number) => {
  let hr = h;
  let suffix = 'a';
  if (h === 0) {
    hr = 12;
  } else if (h === 12) {
    suffix = 'p';
  } else if (h > 12) {
    hr = h - 12;
    suffix = 'p';
  }
  return `${hr}${suffix}`;
};

export const dayTotal = (limits: LimitsMap, day: number) =>
  HOURS.reduce((acc, h) => acc + (limits[day]?.[h] || 0), 0);

export const computeSummary = (limits: LimitsMap) => {
  let weekTotal = 0;
  let peak = 0;
  DAY_ORDER.forEach((d) =>
    HOURS.forEach((h) => {
      const v = limits[d]?.[h] || 0;
      weekTotal += v;
      if (v > peak) peak = v;
    }),
  );
  return { weekTotal, peak };
};

export const isDirty = (limits: LimitsMap, baseline: LimitsMap) =>
  DAY_ORDER.some((d) => HOURS.some((h) => limits[d]?.[h] !== baseline[d]?.[h]));

export const collectChanges = (
  limits: LimitsMap,
  baseline: LimitsMap,
  guids: GuidMap,
) => {
  const updates: { guid: string; limit: number }[] = [];
  DAY_ORDER.forEach((d) =>
    HOURS.forEach((h) => {
      const current = limits[d]?.[h];
      const guid = guids[d]?.[h];
      if (
        guid !== undefined &&
        current !== undefined &&
        current !== baseline[d]?.[h]
      ) {
        updates.push({ guid, limit: current });
      }
    }),
  );
  return updates;
};

// Which (day,hour) cells a bulk Apply targets, given the header selections:
//  - days only  → all hours of those days
//  - hours only → those hours across all days
//  - both       → the intersection
export const computeTargetKeys = (
  selDays: Record<number, boolean>,
  selHours: Record<number, boolean>,
): string[] => {
  const selectedDays = DAY_ORDER.filter((d) => selDays[d]);
  const selectedHours = HOURS.filter((h) => selHours[h]);
  let targetDays: number[] = [];
  let targetHours: number[] = [];
  if (selectedDays.length && selectedHours.length) {
    targetDays = selectedDays;
    targetHours = selectedHours;
  } else if (selectedDays.length) {
    targetDays = selectedDays;
    targetHours = HOURS;
  } else if (selectedHours.length) {
    targetDays = DAY_ORDER;
    targetHours = selectedHours;
  }
  const keys: string[] = [];
  targetDays.forEach((d) =>
    targetHours.forEach((h) => keys.push(cellKey(d, h))),
  );
  return keys;
};
